import { useState, useEffect, useCallback, useContext } from "react";
import UIkit from "uikit";
import CRMService from "../../services/crm.service";
import { AppContexts } from "../../providers";

const pad2 = (n) => String(n).padStart(2, "0");

const formatDateToMMDDYYYY = (raw) => {
  if (!raw) return "";
  const value = String(raw);

  // Handles YYYY-MM-DD and YYYY-MM-DDTHH:mm:ss
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${m}/${d}/${y}`;
  }

  // Normalize Date parseable strings
  const dt = new Date(value);
  if (!Number.isNaN(dt.getTime())) {
    return `${pad2(dt.getMonth() + 1)}/${pad2(dt.getDate())}/${dt.getFullYear()}`;
  }

  return value;
};

const parseMMDDYYYYToISO = (raw) => {
  if (!raw) return "";
  const v = String(raw).trim();
  const m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;

  const month = Number(m[1]);
  const day = Number(m[2]);
  const year = Number(m[3]);
  const dt = new Date(year, month - 1, day);

  if (
    Number.isNaN(dt.getTime()) ||
    dt.getFullYear() !== year ||
    dt.getMonth() !== month - 1 ||
    dt.getDate() !== day
  ) {
    return null;
  }

  return `${year}-${pad2(month)}-${pad2(day)}`;
};

const CustomFieldsSection = ({ entityType, entityId }) => {
  const { user: account } = useContext(AppContexts.AuthContext);
  const [definitions, setDefinitions] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState(null);
  const [editingFieldId, setEditingFieldId] = useState(null);

  const hasCustomFieldsFlag = (account?.flags || []).some((f) => f.name === "crm");

  const loadData = useCallback(async () => {
    if (!entityType || !entityId) return;
    setLoading(true);
    try {
      const [defsRes, valsRes] = await Promise.all([
        CRMService.getCustomFieldDefinitions(entityType),
        CRMService.getCustomFieldValues(entityType, entityId),
      ]);
      setDefinitions(defsRes?.data || []);

      // Build values map: field_definition_id → value
      const valMap = {};
      (valsRes?.data || []).forEach((v) => {
        valMap[v.field_definition_id] = v;
      });
      setValues(valMap);
    } catch (err) {
      console.error("Failed to load custom fields", err);
    }
    setLoading(false);
  }, [entityType, entityId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getValue = (def) => {
    const v = values[def.id];
    if (!v) return "";
    if (def.field_type === "number") return v.value_number ?? "";
    if (def.field_type === "date") return formatDateToMMDDYYYY(v.value_date);
    if (def.field_type === "boolean") return v.value_boolean ?? false;
    return v.value_text ?? "";
  };

  const handleChange = useCallback(
    async (def, rawValue) => {
      let valueToSave = rawValue;
      if (def.field_type === "date") {
        const parsedDate = parseMMDDYYYYToISO(rawValue);
        if (rawValue && parsedDate === null) {
          UIkit.notification("Enter date as MM/DD/YYYY", "warning");
          return;
        }
        valueToSave = parsedDate;
      }

      // Optimistic update
      setValues((prev) => {
        const existing = prev[def.id] || {};
        const updated = { ...existing, field_definition_id: def.id };
        if (def.field_type === "number") updated.value_number = rawValue;
        else if (def.field_type === "date") updated.value_date = valueToSave;
        else if (def.field_type === "boolean") updated.value_boolean = rawValue;
        else updated.value_text = rawValue;
        return { ...prev, [def.id]: updated };
      });

      setSavingField(def.id);
      try {
        await CRMService.upsertCustomFieldValue({
          entity_type: entityType,
          entity_id: entityId,
          field_definition_id: def.id,
          value: valueToSave,
        });
      } catch (err) {
        console.error("Failed to save custom field value", err);
        UIkit.notification("Failed to save", "danger");
      }
      setSavingField(null);
    },
    [entityType, entityId],
  );

  if (loading) {
    return null; // Don't show section while loading
  }

  if (definitions.length === 0) {
    return null; // No fields defined — hide section entirely
  }

  return (
    <div className="crm-section">
      <div className="crm-section-title">
        Custom Fields
        {hasCustomFieldsFlag && (
          <a
            href="/custom-fields"
            className="cf-manage-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Manage
          </a>
        )}
      </div>
      <div className="cf-values-grid">
        {definitions.map((def) => (
          <CustomFieldInput
            key={def.id}
            definition={def}
            value={getValue(def)}
            saving={savingField === def.id}
            isEditing={editingFieldId === def.id}
            onStartEdit={() => setEditingFieldId(def.id)}
            onCancelEdit={() => setEditingFieldId(null)}
            onChange={(val) => handleChange(def, val)}
            onSave={(val) => {
              handleChange(def, val);
              setEditingFieldId(null);
            }}
          />
        ))}
      </div>
    </div>
  );
};

const CustomFieldInput = ({ definition, value, saving, isEditing, onStartEdit, onCancelEdit, onChange, onSave }) => {
  const { field_type, field_label, options_json } = definition;

  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const displayValue = () => {
    if (field_type === "boolean") return value ? "Yes" : "No";
    if (!value && value !== 0) return "Click to add";
    if (field_type === "date") return formatDateToMMDDYYYY(value);
    return String(value);
  };

  if (!isEditing) {
    return (
      <div className="cf-value-row cf-clickable" onClick={onStartEdit}>
        <label className="cf-value-label">{field_label}</label>
        <div className="cf-value-display">{displayValue()}</div>
      </div>
    );
  }

  if (field_type === "boolean") {
    return (
      <div className="cf-value-row">
        <label className="cf-value-label">{field_label}</label>
        <div className="cf-value-input">
          <label className="cf-toggle">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => {
                onSave(e.target.checked);
              }}
            />
            <span className="cf-toggle-slider"></span>
          </label>
          {saving && <span className="cf-saving">saving…</span>}
          <button className="cf-inline-cancel" onClick={onCancelEdit}>Cancel</button>
        </div>
      </div>
    );
  }

  if (field_type === "select") {
    const options = Array.isArray(options_json) ? options_json : [];
    return (
      <div className="cf-value-row">
        <label className="cf-value-label">{field_label}</label>
        <div className="cf-value-input">
          <select
            className="uk-select cf-select"
            value={localValue || ""}
            onChange={(e) => {
              setLocalValue(e.target.value);
              onSave(e.target.value);
            }}
          >
            <option value="">—</option>
            {options.map((opt, i) => (
              <option key={i} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          {saving && <span className="cf-saving">saving…</span>}
          <button className="cf-inline-cancel" onClick={onCancelEdit}>Cancel</button>
        </div>
      </div>
    );
  }

  if (field_type === "textarea") {
    return (
      <div className="cf-value-row">
        <label className="cf-value-label">{field_label}</label>
        <div className="cf-value-input">
          <textarea
            className="uk-textarea cf-textarea"
            rows={2}
            value={localValue || ""}
            onChange={(e) => setLocalValue(e.target.value)}
          />
          {saving && <span className="cf-saving">saving…</span>}
          <button className="cf-inline-save" onClick={() => onSave(localValue)}>Save</button>
          <button className="cf-inline-cancel" onClick={onCancelEdit}>Cancel</button>
        </div>
      </div>
    );
  }

  if (field_type === "date") {
    return (
      <div className="cf-value-row">
        <label className="cf-value-label">{field_label}</label>
        <div className="cf-value-input">
          <input
            className="uk-input cf-input"
            type="text"
            placeholder="MM/DD/YYYY"
            value={localValue || ""}
            onChange={(e) => setLocalValue(e.target.value)}
          />
          {saving && <span className="cf-saving">saving…</span>}
          <button className="cf-inline-save" onClick={() => onSave(localValue)}>Save</button>
          <button className="cf-inline-cancel" onClick={onCancelEdit}>Cancel</button>
        </div>
      </div>
    );
  }

  if (field_type === "number") {
    return (
      <div className="cf-value-row">
        <label className="cf-value-label">{field_label}</label>
        <div className="cf-value-input">
          <input
            className="uk-input cf-input"
            type="number"
            value={localValue ?? ""}
            onChange={(e) => setLocalValue(e.target.value)}
          />
          {saving && <span className="cf-saving">saving…</span>}
          <button className="cf-inline-save" onClick={() => onSave(localValue)}>Save</button>
          <button className="cf-inline-cancel" onClick={onCancelEdit}>Cancel</button>
        </div>
      </div>
    );
  }

  // Default: text
  return (
    <div className="cf-value-row">
      <label className="cf-value-label">{field_label}</label>
      <div className="cf-value-input">
        <input
          className="uk-input cf-input"
          type="text"
          value={localValue || ""}
          onChange={(e) => setLocalValue(e.target.value)}
        />
        {saving && <span className="cf-saving">saving…</span>}
        <button className="cf-inline-save" onClick={() => onSave(localValue)}>Save</button>
        <button className="cf-inline-cancel" onClick={onCancelEdit}>Cancel</button>
      </div>
    </div>
  );
};

export default CustomFieldsSection;
